import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { PageLayout } from '../components/common/PageLayout'
import { Section, Button, GameCard } from '../components/common/DesignComponents'
import { TabNavigation, Breadcrumb } from '../components/common/NavigationComponents'
import { Alert } from '../components/common/LayoutPatterns'

interface GameEvent {
  id: string
  name: string
  description: string
  eventType: string
  startDate: string
  endDate: string
  rewards: Array<{ id: string; name: string; description: string; amount: number }>
  maxParticipants?: number
  currentParticipants?: number
  tier?: {
    name: string
    color: string
    multiplier: number
  }
}

interface Season {
  name: string
  season: string
  startDate: string
  endDate: string
}

export const EventCalendarPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>()
  const navigate = useNavigate()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [events, setEvents] = useState<GameEvent[]>([])
  const [seasons, setSeasons] = useState<Season[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<GameEvent | null>(null)
  const [joining, setJoining] = useState<string | null>(null)

  useEffect(() => {
    fetchEventData()
  }, [])

  const fetchEventData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [eventsRes, seasonsRes] = await Promise.all([
        axios.get('/api/events/calendar'),
        axios.get('/api/events/seasonal/all'),
      ])

      setEvents(eventsRes.data.data)
      setSeasons(seasonsRes.data.data)
    } catch (err) {
      console.error('Failed to fetch events:', err)
      setError('Failed to load events. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinEvent = async (eventId: string) => {
    if (!childId) return

    try {
      setJoining(eventId)
      await axios.post(`/api/events/${eventId}/join`, {
        childId,
        eventId,
      })

      // Refresh events after joining
      await fetchEventData()
      setSelectedEvent(null)
    } catch (err) {
      console.error('Failed to join event:', err)
      setError('Failed to join event. Try again.')
    } finally {
      setJoining(null)
    }
  }

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getEventsForDate = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return events.filter((event) => {
      const startDate = new Date(event.startDate).toLocaleDateString('en-CA')
      const endDate = new Date(event.endDate).toLocaleDateString('en-CA')
      return dateStr >= startDate && dateStr <= endDate
    })
  }

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin text-5xl mb-4">📅</div>
            <p className="text-purple-600 font-semibold">Loading events...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  const daysInMonth = getDaysInMonth(currentMonth)
  const firstDay = getFirstDayOfMonth(currentMonth)
  const calendarDays = []

  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null)
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day)
  }

  return (
    <PageLayout>
      <Section>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
            📅 Event Calendar
          </h1>
          <p className="text-gray-600">Discover exciting events and seasonal adventures</p>
        </div>

        {error && (
          <Alert
            type="error"
            title="Event Error"
            message={error}
            icon="❌"
            className="mb-6"
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Calendar Section */}
          <div className="lg:col-span-2 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-100 p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-purple-100 rounded transition text-purple-600 font-bold text-xl"
              >
                ◀
              </button>
              <h2 className="text-xl font-bold text-purple-600">
                {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-purple-100 rounded transition text-purple-600 font-bold text-xl"
              >
                ▶
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="text-center font-bold text-purple-600 text-sm py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map((day, index) => {
                const dayEvents = day ? getEventsForDate(day) : []
                const isToday =
                  day === new Date().getDate() &&
                  currentMonth.getMonth() === new Date().getMonth() &&
                  currentMonth.getFullYear() === new Date().getFullYear()

                return (
                  <div
                    key={index}
                    className={`aspect-square p-2 rounded-lg border-2 transition cursor-pointer ${
                      !day
                        ? 'bg-white/50'
                        : isToday
                          ? 'border-purple-400 bg-purple-100 font-bold'
                          : dayEvents.length > 0
                            ? 'border-pink-400 bg-pink-100'
                            : 'border-purple-200 bg-white hover:border-purple-300'
                    }`}
                    onClick={() => {
                      if (dayEvents.length > 0) {
                        setSelectedEvent(dayEvents[0])
                      }
                    }}
                  >
                    {day && (
                      <div className="h-full flex flex-col">
                        <p className="text-sm font-bold text-purple-600">{day}</p>
                        {dayEvents.length > 0 && (
                          <div className="text-xs text-pink-600 font-semibold truncate mt-auto">
                            {dayEvents.length} 📌
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Upcoming Events Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-100 p-6">
            <h3 className="text-lg font-bold mb-4 text-blue-600">Upcoming Events</h3>

            {events.length === 0 ? (
              <p className="text-gray-600 text-sm">No upcoming events</p>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    className="p-3 bg-white rounded-lg border-2 border-blue-100 hover:border-blue-300 transition cursor-pointer"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <p className="font-bold text-sm text-blue-600 truncate">
                      {event.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {event.eventType}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      ⏰ {new Date(event.endDate).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active Seasons */}
        <div>
          <h3 className="text-2xl font-bold text-purple-600 mb-4">Active Seasons</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {seasons.map((season) => (
              <GameCard
                key={season.season}
                title={season.name}
                icon="🎪"
                badges={[new Date(season.startDate).toLocaleDateString()]}
                className="bg-gradient-to-br from-purple-50 to-pink-50"
              >
                <p className="text-sm text-gray-600 mb-4">
                  {new Date(season.startDate).toLocaleDateString()} to{' '}
                  {new Date(season.endDate).toLocaleDateString()}
                </p>
                <Button
                  onClick={() => handleJoinEvent(season.season)}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  🎭 Join Season
                </Button>
              </GameCard>
            ))}
          </div>
        </div>
      </Section>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white border-2 border-purple-200 rounded-xl max-w-md w-full p-6 space-y-4 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-purple-600">
                  {selectedEvent.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedEvent.eventType}
                </p>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg"
              >
                ✕
              </button>
            </div>

            <p className="text-gray-600">{selectedEvent.description}</p>

            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                ⏰ <span>
                  {new Date(selectedEvent.startDate).toLocaleDateString()} -{' '}
                  {new Date(selectedEvent.endDate).toLocaleDateString()}
                </span>
              </div>
              {selectedEvent.currentParticipants && (
                <div className="flex items-center gap-2">
                  👥 <span>
                    {selectedEvent.currentParticipants} /{' '}
                    {selectedEvent.maxParticipants} Participants
                  </span>
                </div>
              )}
            </div>

            {selectedEvent.rewards.length > 0 && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-100">
                <h4 className="font-bold text-sm mb-2 flex items-center gap-2 text-amber-600">
                  🏆 Rewards
                </h4>
                <div className="space-y-1 text-sm text-gray-600">
                  {selectedEvent.rewards.map((reward) => (
                    <div key={reward.id}>
                      {reward.name} (+{reward.amount})
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => setSelectedEvent(null)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800"
              >
                Close
              </Button>
              <Button
                onClick={() => handleJoinEvent(selectedEvent.id)}
                disabled={joining === selectedEvent.id}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
              >
                {joining === selectedEvent.id ? '⏳ Joining...' : '✨ Join Event'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default EventCalendarPage
